import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ago = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 36e5));
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
};

// Dashboard tile: which slabs shoppers are emailing to their designers, with their notes.
export const DesignerSends = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/designer-handoffs", { params: { days: 30 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, top: [] }));
  }, []);
  if (!data) return null;
  const rows = open ? data.items : data.items.slice(0, 4);
  return (
    <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-800/40 p-4" data-testid="designer-sends">
      <div className="flex items-center gap-2">
        <PaperPlaneTilt size={16} weight="bold" className="text-sky-400" />
        <div className="text-xs uppercase tracking-widest text-zinc-500">Sent to designers · 30 days</div>
        <span className="font-mono-data ml-auto text-sm font-bold text-sky-400" data-testid="designer-sends-total">
          {data.total}
        </span>
      </div>
      {data.total === 0 ? (
        <p className="mt-3 text-sm text-zinc-500" data-testid="designer-sends-empty">
          No slabs sent to designers yet — the button lives on every public slab page.
        </p>
      ) : (
        <>
          {data.top.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="designer-sends-top">
              {data.top.map((t) => (
                <a
                  key={t.key}
                  href={`/inventory/${t.key}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`designer-sends-top-${t.key}`}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-500/30 bg-sky-500/10 px-3 py-1 text-xs text-sky-200 hover:border-sky-400"
                >
                  {t.color || t.key}
                  <span className="font-mono-data font-bold">{t.count}×</span>
                </a>
              ))}
            </div>
          )}
          <div className="mt-3 grid gap-2 lg:grid-cols-2">
            {rows.map((r) => (
              <div key={r.id} data-testid={`designer-send-${r.id}`} className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <a href={`/inventory/${r.key}`} target="_blank" rel="noreferrer" className="truncate text-sm font-semibold text-zinc-100 hover:text-sky-300">
                    {r.color || r.key}
                  </a>
                  <span className="font-mono-data ml-auto text-[11px] text-zinc-500">{ago(r.created_at)}</span>
                </div>
                <div className="mt-1 truncate text-xs text-zinc-400">
                  {r.your_name || "A shopper"} → {r.designer_name ? `${r.designer_name} · ` : ""}
                  <a href={`mailto:${r.designer_email}`} className="text-zinc-300 hover:text-sky-300">{r.designer_email}</a>
                  {r.your_email && (
                    <>
                      {" "}· cc <a href={`mailto:${r.your_email}`} className="text-zinc-300 hover:text-sky-300">{r.your_email}</a>
                    </>
                  )}
                </div>
                {r.note && (
                  <p className="mt-2 border-l-2 border-sky-500/50 pl-2 text-xs italic leading-relaxed text-zinc-300" data-testid={`designer-send-note-${r.id}`}>
                    “{r.note}”
                  </p>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {r.followed_up_at ? (
                    <span data-testid={`designer-send-followed-${r.id}`} className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                      <Check size={12} weight="bold" /> Followed up {ago(r.followed_up_at)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`designer-send-followup-${r.id}`}
                      onClick={() => setDraft({ row: r, subject: r.draft.subject, body: r.draft.body })}
                      className="flex h-8 items-center gap-1.5 rounded-md bg-sky-500/15 px-3 text-[10px] font-bold uppercase tracking-widest text-sky-300 hover:bg-sky-500/25"
                    >
                      <PaperPlaneTilt size={12} weight="bold" /> Follow up
                    </button>
                  )}
                  <a
                    href={`mailto:${r.designer_email}?${r.your_email ? `cc=${encodeURIComponent(r.your_email)}&` : ""}subject=${encodeURIComponent(r.draft.subject)}&body=${encodeURIComponent(r.draft.body)}`}
                    data-testid={`designer-send-mailto-${r.id}`}
                    className="ml-auto text-[11px] text-zinc-500 hover:text-sky-300"
                  >
                    Open in my mail app
                  </a>
                </div>
              </div>
            ))}
          </div>
          {data.items.length > 4 && (
            <button type="button" onClick={() => setOpen((v) => !v)} data-testid="designer-sends-more" className="mt-3 text-xs text-sky-300 hover:underline">
              {open ? "Show fewer" : `Show all ${data.items.length}`}
            </button>
          )}
          <Link to="/inventory" className="sr-only">Inventory</Link>
        </>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center" data-testid="designer-followup-dialog" onClick={() => setDraft(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                const { data: res } = await api.post(`/designer-handoffs/${draft.row.id}/follow-up`, { subject: draft.subject, body: draft.body });
                setData((d) => ({ ...d, items: d.items.map((x) => (x.id === draft.row.id ? { ...x, ...res } : x)) }));
                toast.success(res.sent ? `Follow-up sent to ${draft.row.designer_name || draft.row.designer_email}` : "Follow-up logged (mail is switched off)");
                setDraft(null);
              } catch (err) {
                toast.error(err.response?.data?.detail || "Could not send the follow-up");
              }
              setBusy(false);
            }}
            className="w-full max-w-xl rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold">Follow up with the designer</h2>
                <p className="mt-1 text-xs text-zinc-500">
                  To {draft.row.designer_email}
                  {draft.row.your_email ? ` · cc ${draft.row.your_email}` : ""} — edit anything, then send from the shop.
                </p>
              </div>
              <button type="button" onClick={() => setDraft(null)} data-testid="designer-followup-close" className="text-zinc-500 hover:text-white">
                <X size={18} weight="bold" />
              </button>
            </div>
            <input
              data-testid="designer-followup-subject"
              value={draft.subject}
              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              className="mt-5 h-11 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100"
            />
            <textarea
              data-testid="designer-followup-body"
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              rows={10}
              className="mt-3 w-full rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm leading-relaxed text-zinc-100"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setDraft(null)} className="h-11 rounded-md border border-zinc-700 px-4 text-[11px] uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
                Cancel
              </button>
              <button type="submit" disabled={busy} data-testid="designer-followup-send" className="flex h-11 items-center gap-2 rounded-md bg-sky-500 px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-sky-400 disabled:opacity-50">
                <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send follow-up"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
